import React, { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDownIcon, CrossIcon, SearchIcon } from '../../../assets/icons/icons';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { cx } from '../../../utils/cx';
import styles from './FilterBar.module.scss';

export function FilterBar({ children }: { children: ReactNode }) {
    return <section className={styles.toolbar}>{children}</section>;
}

export function FilterSpacer() {
    return <span className={styles.spacer} aria-hidden="true" />;
}

interface FilterSearchProps {
    value: string;
    placeholder: string;
    label?: string;
    onChange: (value: string) => void;
}

export function FilterSearch({ value, placeholder, label = 'Поиск', onChange }: FilterSearchProps) {
    return (
        <div className={styles.search}>
            <SearchIcon className={styles.searchIcon} />
            <input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                autoComplete="off"
                aria-label={label}
            />
            {value && (
                <button
                    type="button"
                    className={styles.clear}
                    onClick={() => onChange('')}
                    aria-label="Очистить поиск"
                >
                    <CrossIcon />
                </button>
            )}
        </div>
    );
}

interface FilterResetProps {
    active: boolean;
    onClick: () => void;
}

export function FilterReset({ active, onClick }: FilterResetProps) {
    return (
        <button type="button" className={styles.reset} onClick={onClick} disabled={!active}>
            Сбросить
        </button>
    );
}

interface FilterMenuProps {
    label: string;
    active?: boolean;
    wide?: boolean;
    align?: 'left' | 'right';
    children: (close: () => void) => ReactNode;
}

export function FilterMenu({
    label,
    active = false,
    wide = false,
    align = 'left',
    children,
}: FilterMenuProps) {
    const [open, setOpen] = useState(false);
    const ref = useOutsideClick<HTMLDivElement>(() => setOpen(false));

    const close = () => setOpen(false);

    return (
        <div className={cx(styles.filter, open && styles.open)} ref={ref}>
            <button
                type="button"
                className={cx(styles.trigger, active && styles.set)}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
            >
                <span className={styles.cur}>{label}</span>
                <ChevronDownIcon className={styles.caret} />
            </button>

            {open && (
                <div
                    className={cx(
                        styles.menu,
                        wide && styles.wide,
                        align === 'right' && styles.menuRight,
                    )}
                >
                    {children(close)}
                </div>
            )}
        </div>
    );
}

export interface FilterOption<T extends string> {
    value: T;
    label: string;
    count?: number;
    dotColor?: string;
}

interface FilterOptionsProps<T extends string> {
    options: FilterOption<T>[];
    value: T;
    onChange: (value: T) => void;
    close: () => void;
    searchable?: boolean;
    searchPlaceholder?: string;
}

export function FilterOptions<T extends string>({
    options,
    value,
    onChange,
    close,
    searchable = false,
    searchPlaceholder = 'Начните вводить название',
}: FilterOptionsProps<T>) {
    const [query, setQuery] = useState('');

    const withDots = options.some((option) => option.dotColor !== undefined);

    const shown = useMemo(() => {
        const text = query.trim().toLowerCase();
        if (!searchable || !text) return options;
        return options.filter((option) => option.label.toLowerCase().includes(text));
    }, [options, query, searchable]);

    const pick = (next: T) => {
        onChange(next);
        close();
    };

    return (
        <>
            {searchable && (
                <div className={styles.menuSearch}>
                    <input
                        className={styles.menuSearchInput}
                        value={query}
                        placeholder={searchPlaceholder}
                        autoComplete="off"
                        aria-label="Поиск по списку"
                        autoFocus
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && shown[0]) {
                                event.preventDefault();
                                pick(shown[0].value);
                            }
                            if (event.key === 'Escape') close();
                        }}
                    />
                </div>
            )}

            {shown.length === 0 ? (
                <div className={styles.menuEmpty}>Ничего не найдено</div>
            ) : (
                <div className={styles.list}>
                    {shown.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={cx(styles.option, value === option.value && styles.on)}
                            onClick={() => pick(option.value)}
                        >
                            {withDots && (
                                <i className={styles.dot} style={{ background: option.dotColor ?? 'transparent' }} />
                            )}
                            {option.label}
                            {option.count !== undefined && <span className={styles.count}>{option.count}</span>}
                        </button>
                    ))}
                </div>
            )}
        </>
    );
}

interface FilterSelectProps<T extends string> {
    options: FilterOption<T>[];
    value: T;
    allValue: T;
    onChange: (value: T) => void;
    wide?: boolean;
    align?: 'left' | 'right';
    searchable?: boolean;
    searchPlaceholder?: string;
}

export function FilterSelect<T extends string>({
    options,
    value,
    allValue,
    onChange,
    wide,
    align,
    searchable,
    searchPlaceholder,
}: FilterSelectProps<T>) {
    const current = options.find((option) => option.value === value);

    return (
        <FilterMenu
            label={current?.label ?? ''}
            active={value !== allValue}
            wide={wide}
            align={align}
        >
            {(close) => (
                <FilterOptions
                    options={options}
                    value={value}
                    onChange={onChange}
                    close={close}
                    searchable={searchable}
                    searchPlaceholder={searchPlaceholder}
                />
            )}
        </FilterMenu>
    );
}